import React, { useState } from "react";
import ImageUploader from "./ImageUploader";
import WaitingModal from "./WaitingModal";
import { FieldDate, FieldText } from "./EditableField";
import { APPLIANCE_TYPE_OPTIONS } from "../../constants/applianceTypes";
import logger from "../../services/logger";
import UploadServiceInstance from "../../services/upload.service";
import SearchServiceInstance from "../../services/search.service";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 23, 44, 0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
  padding: "16px",
};

const modalStyle = {
  background: "#ffffff",
  borderRadius: "16px",
  padding: "20px",
  maxWidth: "640px",
  width: "100%",
  position: "relative",
  maxHeight: "85vh",
  overflowY: "auto",
};

const closeButtonStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  border: "none",
  background: "transparent",
  fontSize: "22px",
  cursor: "pointer",
  color: "#64748b",
};

const InvoiceScannerModal = ({
  isOpen = false,
  onClose = () => {},
  onImageUpload = () => {},
  onConfirm = () => {},
  onPopinConfirm = () => {},
}) => {
  const [pict, setPict] = useState(null);
  const [pictUrl, setPictUrl] = useState("");
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [manualFields, setManualFields] = useState({
    type: "",
    marque: "",
    modele: "",
    date_achat: "",
  });
  const [uploaderKey, setUploaderKey] = useState(0);

  const handleImageUpload = (uploadedUrl, uploadedImage) => {
    setPict(uploadedImage);
    setPictUrl(uploadedUrl || "");
    onImageUpload(uploadedUrl, uploadedImage);
    setResult(null);
    setErrorMessage("");
  };

  const handleAnalyzeClick = async () => {
    if (!pict) {
      setErrorMessage("Ajoute une photo ou un PDF pour lancer l'analyse.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    setResult(null);
    try {
      const uploadResult = await UploadServiceInstance.uploadTempFile(pict);
      const url = uploadResult ? uploadResult.fileUrl : "";
      const data = await SearchServiceInstance.checkInvoiceByImage(url);
      const nextResult = data || null;
      const normalizeDate = (value) => {
        if (!value || typeof value !== "string") return "";
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
      };
      setResult(nextResult);
      if (nextResult) {
        setManualFields({
          type: nextResult.type || "",
          marque: nextResult.marque || "",
          modele: nextResult.modele || "",
          date_achat: normalizeDate(nextResult.date_achat),
        });
      }
    } catch (error) {
      logger.log("Erreur analyse facture :", error);
      setErrorMessage("Une erreur est survenue lors de l'analyse.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!result) return;
    const payload = {
      type: manualFields.type || result?.type || "",
      marque: manualFields.marque || result?.marque || "",
      modele: manualFields.modele || result?.modele || "",
      date_achat: manualFields.date_achat || result?.date_achat || "",
      pict: pictUrl || "",
    };
    onConfirm({
      ...payload,
      model: payload.modele || "",
    });
    onPopinConfirm(payload);
    onClose();
  };

  const handleManualChange = (field) => (event) => {
    const value = event.target.value;
    setManualFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRetake = () => {
    setPict(null);
    setPictUrl("");
    setResult(null);
    setErrorMessage("");
    setManualFields({ type: "", marque: "", modele: "", date_achat: "" });
    setUploaderKey((prev) => prev + 1);
    onImageUpload(null, null);
  };


  if (!isOpen) return null;

  return (
    <>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
          <button
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="Fermer"
          >
            ×
          </button>
          <ImageUploader
            key={uploaderKey}
            onImageUpload={handleImageUpload}
          />
          <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={handleAnalyzeClick}
              style={{
                border: "none",
                borderRadius: "999px",
                background: "var(--color-green)",
                color: "#fff",
                padding: "10px 18px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {isLoading ? "Analyse..." : "Analyser la facture"}
            </button>
          </div>
          {errorMessage && (
            <div style={{ marginTop: "10px", color: "#b91c1c" }}>
              {errorMessage}
            </div>
          )}
          {result && (
            <>
              <div style={{ marginTop: "12px" }}>
                <div style={{ marginBottom: "8px" }}>
                  <label style={{ display: "block", marginBottom: "6px" }}>
                    Type
                  </label>
                  <select
                    value={manualFields.type}
                    onChange={handleManualChange("type")}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5f5",
                    }}
                  >
                    <option value="">Sélectionner un type</option>
                    {APPLIANCE_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldText
                  label="Marque"
                  value={manualFields.marque}
                  onChange={handleManualChange("marque")}
                />
                <FieldText
                  label="Modele"
                  value={manualFields.modele}
                  onChange={handleManualChange("modele")}
                />
                <FieldDate
                  label="Date achat"
                  value={manualFields.date_achat}
                  onChange={handleManualChange("date_achat")}
                />
              </div>
              <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  style={{
                    border: "none",
                    borderRadius: "999px",
                    background: "var(--color-green)",
                    color: "#fff",
                    padding: "10px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Confirmer
                </button>
                <button
                  type="button"
                  onClick={handleRetake}
                  disabled={isLoading}
                  style={{
                    border: "1px solid #94a3b8",
                    borderRadius: "999px",
                    background: "#fff",
                    color: "#475569",
                    padding: "10px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Reprendre la photo
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {isLoading && <WaitingModal />}
    </>
  );
};

export default InvoiceScannerModal;
